import type { ChatPlayerRole, ChatPlayerTranscriptSource } from "@ui/components/core/ChatPlayer";

const AGENT_SPEAKER_ID = "speaker_0";

type SttWord = {
  end: number;
  speaker_id?: string | null;
  start: number;
  text: string;
};

type SttResponse = {
  language_code?: string;
  words: SttWord[];
};

type SttSegment = ChatPlayerTranscriptSource["segments"][number] & {
  speaker: {
    id: string;
    name: string;
  };
  words: NonNullable<ChatPlayerTranscriptSource["segments"][number]["words"]>;
};

function getSttResponse(value: unknown) {
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const sttResponse = value as Partial<SttResponse>;
  return Array.isArray(sttResponse.words) ? (sttResponse as SttResponse) : null;
}

function getSpeakerId(word: SttWord, currentSpeakerId?: string) {
  if (word.speaker_id) {
    return word.speaker_id;
  }

  return currentSpeakerId ?? null;
}

function getSpeakerName(speakerId: string) {
  return speakerId.replace(/^speaker_(\d+)$/, "Speaker $1");
}

export function createChatPlayerSourceFromSttResponse(sttResponse: unknown) {
  const response = getSttResponse(sttResponse);

  if (!response) {
    return null;
  }

  const segments: SttSegment[] = [];
  const speakerRoles: Record<string, ChatPlayerRole> = {};
  let currentSegment: SttSegment | null = null;

  response.words.forEach(word => {
    const speakerId = getSpeakerId(word, currentSegment?.speaker.id);

    if (!speakerId) {
      return;
    }

    speakerRoles[speakerId] = speakerId === AGENT_SPEAKER_ID ? "assistant" : "user";

    if (currentSegment?.speaker.id !== speakerId) {
      currentSegment = {
        text: "",
        start_time: word.start,
        end_time: word.end,
        speaker: {
          id: speakerId,
          name: getSpeakerName(speakerId),
        },
        words: [],
      };
      segments.push(currentSegment);
    }

    currentSegment.text += word.text;
    currentSegment.end_time = word.end;
    currentSegment.words.push({
      text: word.text,
      start_time: word.start,
      end_time: word.end,
    });
  });

  if (segments.length === 0) {
    return null;
  }

  return {
    language_code: response.language_code,
    segments,
    speakerRoles,
  } satisfies ChatPlayerTranscriptSource;
}
